/**
 * Enhancer for YouTube™ — player controls / theming for YouTube
 *
 * Declares a single WAR group with no `use_dynamic_url`, but matched only to YouTube
 * origins (www.youtube.com and www.youtube-nocookie.com). It globs a per-locale
 * messages.json, the theme stylesheets under `css/themes/` and the bundled `vendor/`
 * assets. Those resources are genuinely unreachable from any other origin, so this is
 * only detectable while running on YouTube — a negative anywhere else proves nothing.
 *
 * Manifest verified against version 3.0.19.
 */

import { probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'ponfpcnoihfmfllpaingbgckeeldkhle';

export const enhancerForYoutube: ExtensionDefinition = {
  id: ID,
  name: 'Enhancer for YouTube™',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 1_000_000,
  requiredOrigin: 'https://www.youtube.com',
  notes:
    'All WAR scoped to YouTube origins, no dynamic URL. Detectable only from ' +
    'www.youtube.com; the theme stylesheet fetch is conclusive there.',
  signals: [
    {
      id: 'war-theme-main-css',
      kind: 'web-accessible-resource',
      description:
        'Fetch css/themes/main.css — the base theme stylesheet, exposed only to YouTube ' +
        'origins.',
      weight: 1,
      run: () => probeFetch(ID, 'css/themes/main.css'),
    },
    {
      id: 'war-vendor-deep-dark-discord',
      kind: 'web-accessible-resource',
      description:
        'Fetch vendor/themes/youtube-deep-dark-discord.css — a vendor-named theme, a ' +
        'second scoped path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'vendor/themes/youtube-deep-dark-discord.css'),
    },
    {
      id: 'war-locale-messages',
      kind: 'web-accessible-resource',
      description:
        'Fetch _locales/en_GB/messages.json — the localisation catalogue, an independent ' +
        'scoped path.',
      weight: 0.5,
      run: () => probeFetch(ID, '_locales/en_GB/messages.json'),
    },
  ],
};
